import type { GetAttendanceLogResponse, GetMakeupResponse } from "@/openapi/generated";
import { formatDateOnlySafe, safeParseDate } from "@/lib/utils/date/date";
import { formatYmdInTimeZone } from "@/lib/utils/date/studio-clock";

const YMD_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;
/** Years below this are .NET/SQL placeholders (0001-01-01) or the JS Date.UTC 0–99 → 1901 mapping. */
const PLACEHOLDER_YEAR_MAX = 1910;

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

function ymdFromUtcDate(date: Date): string {
  return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`;
}

function isUtcMidnightInstant(date: Date): boolean {
  return (
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0
  );
}

function yearFromYmd(ymd: string): number | null {
  if (!YMD_PREFIX.test(ymd)) return null;
  const year = Number(ymd.slice(0, 4));
  return Number.isFinite(year) ? year : null;
}

/** True for missing, invalid, or defaulted makeup dates such as 0001-01-01 / Jan 1, 1901. */
export function isPlaceholderMakeupDate(
  value: Date | string | null | undefined,
): boolean {
  if (value == null || value === "") return true;

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return true;
    const ymd = trimmed.slice(0, 10);
    const year = yearFromYmd(ymd);
    if (year != null) return year < PLACEHOLDER_YEAR_MAX;
  }

  const parsed = safeParseDate(value);
  if (!parsed) return true;
  return Math.min(parsed.getFullYear(), parsed.getUTCFullYear()) < PLACEHOLDER_YEAR_MAX;
}

/**
 * Calendar YYYY-MM-DD for makeup date-only API fields.
 * Date-only UTC midnight uses the UTC calendar; datetimes use the studio zone.
 */
export function makeupDateOnlyYmd(
  value: Date | string | null | undefined,
  timeZone?: string | null,
): string | null {
  if (isPlaceholderMakeupDate(value)) return null;

  if (typeof value === "string") {
    const ymd = value.trim().slice(0, 10);
    if (yearFromYmd(ymd) != null) return ymd;
  }

  const parsed = safeParseDate(value);
  if (!parsed) return null;
  if (isUtcMidnightInstant(parsed)) return ymdFromUtcDate(parsed);
  return formatYmdInTimeZone(parsed, timeZone);
}

export function formatMakeupDateOnly(
  value: Date | string | null | undefined,
  options?: Intl.DateTimeFormatOptions,
  locale = "en-US",
): string {
  const ymd = makeupDateOnlyYmd(value);
  if (!ymd) return "";
  return formatDateOnlySafe(ymd, options, locale);
}

export function formatMakeupWeekdayDate(
  value: Date | string | null | undefined,
): string {
  return formatMakeupDateOnly(value, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function resolveMakeupMissedDate(
  item: Pick<GetMakeupResponse, "sourceSessionDate" | "attendanceLogId">,
  dateByAttendanceLogId?: ReadonlyMap<string, Date | string>,
): Date | string | null {
  if (!isPlaceholderMakeupDate(item.sourceSessionDate)) {
    return item.sourceSessionDate;
  }
  const logId = item.attendanceLogId?.trim();
  if (!logId || !dateByAttendanceLogId) return null;
  return dateByAttendanceLogId.get(logId) ?? null;
}

export function buildAttendanceLogDateMap(
  logs: Array<Pick<GetAttendanceLogResponse, "id" | "date">>,
): Map<string, Date | string> {
  const map = new Map<string, Date | string>();
  for (const log of logs) {
    const id = log.id?.trim();
    if (!id || isPlaceholderMakeupDate(log.date)) continue;
    map.set(id, log.date);
  }
  return map;
}

export function applyMissedSessionDates(
  items: GetMakeupResponse[],
  dateByAttendanceLogId?: ReadonlyMap<string, Date | string>,
): GetMakeupResponse[] {
  if (items.length === 0) return items;

  return items.map((item) => {
    const resolved = resolveMakeupMissedDate(item, dateByAttendanceLogId);
    if (resolved == null || resolved === item.sourceSessionDate) return item;
    const parsed = safeParseDate(resolved);
    if (!parsed) return item;
    return { ...item, sourceSessionDate: parsed };
  });
}

export function makeupItemsNeedMissedDateHydration(
  items: GetMakeupResponse[],
): boolean {
  return items.some(
    (item) =>
      isPlaceholderMakeupDate(item.sourceSessionDate) &&
      Boolean(item.attendanceLogId?.trim()) &&
      item.student?.id != null,
  );
}
